/**
 * ESG Community Engagement Service
 * Comprehensive stakeholder relations and community development system
 * Following industry standards for social license to operate and community impact
 * 
 * Standards Compliance:
 * - GRI 413: Local Communities
 * - SASB Social Capital Standards
 * - UN Global Compact Principle 1 (Human Rights)
 * - ISO 26000: Community Involvement and Development
 * - ICMM Community Development Toolkit
 * - IFC Performance Standards on Social & Environmental Sustainability
 */

const ESG_COMMUNITY_ENGAGEMENT_KEY = 'esg-community-engagement-v1';

// Industry-standard seed data for ESG community engagement
const esgCommunityEngagementSeed = {
  // Community Engagement Programs
  programs: [
    {
      id: 'cep-001',
      name: 'Indigenous Knowledge Preservation Initiative',
      category: 'Cultural Preservation',
      region: 'NA',
      status: 'active',
      startDate: '2025-01-15T00:00:00Z',
      endDate: '2025-12-31T23:59:59Z',
      budget: 450000,
      impactScore: 87.4,
      participantCount: 342,
      communityPartners: ['Tribal Council of Nations', 'Indigenous Heritage Foundation', 'First Nations Cultural Center'],
      sdgAlignment: ['SDG 4: Quality Education', 'SDG 11: Sustainable Communities'],
      outcomes: {
        documented: 45,
        trained: 234,
        preserved: 18,
        engaged: 567
      },
      stakeholderSatisfaction: 91.2,
      description: 'Comprehensive program to document, preserve, and transmit traditional indigenous knowledge through digital archives and community workshops.',
      riskLevel: 'Low',
      socialLicense: 94.6,
      lastAssessment: '2025-09-15T14:30:00Z'
    },
    {
      id: 'cep-002',
      name: 'Community Economic Development Fund',
      category: 'Economic Development',
      region: 'LATAM',
      status: 'active',
      startDate: '2024-06-01T00:00:00Z',
      endDate: '2026-05-31T23:59:59Z',
      budget: 2400000,
      impactScore: 82.8,
      participantCount: 1247,
      communityPartners: ['Local Entrepreneurs Association', 'Women Business Network', 'Youth Development Cooperative'],
      sdgAlignment: ['SDG 1: No Poverty', 'SDG 8: Decent Work', 'SDG 5: Gender Equality'],
      outcomes: {
        businessesSupported: 156,
        jobsCreated: 423,
        womenBeneficiaries: 578,
        youthTrained: 289
      },
      stakeholderSatisfaction: 86.7,
      description: 'Microfinance and business development program supporting local entrepreneurs with focus on women and youth empowerment.',
      riskLevel: 'Medium',
      socialLicense: 89.3,
      lastAssessment: '2025-09-10T11:20:00Z'
    },
    {
      id: 'cep-003',
      name: 'Sustainable Agriculture Training Program',
      category: 'Environmental Sustainability',
      region: 'APAC',
      status: 'active',
      startDate: '2025-03-01T00:00:00Z',
      endDate: '2025-11-30T23:59:59Z',
      budget: 675000,
      impactScore: 79.6,
      participantCount: 834,
      communityPartners: ['Farmers Cooperative Union', 'Sustainable Agriculture Institute', 'Environmental Conservation Group'],
      sdgAlignment: ['SDG 2: Zero Hunger', 'SDG 15: Life on Land', 'SDG 13: Climate Action'],
      outcomes: {
        farmersTrained: 834,
        hectaresConverted: 2450,
        yieldImprovement: 34.2,
        carbonSequestered: 1890
      },
      stakeholderSatisfaction: 88.4,
      description: 'Training program for sustainable farming practices, soil conservation, and climate-resilient agriculture techniques.',
      riskLevel: 'Low',
      socialLicense: 92.1,
      lastAssessment: '2025-09-18T09:45:00Z'
    },
    {
      id: 'cep-004',
      name: 'Digital Inclusion and Skills Development',
      category: 'Digital Equity',
      region: 'EU',
      status: 'planning',
      startDate: '2025-11-01T00:00:00Z',
      endDate: '2026-10-31T23:59:59Z',
      budget: 890000,
      impactScore: 0,
      participantCount: 0,
      communityPartners: ['Digital Literacy Foundation', 'Community Tech Centers', 'Senior Citizens Association'],
      sdgAlignment: ['SDG 4: Quality Education', 'SDG 9: Industry Innovation', 'SDG 10: Reduced Inequalities'],
      outcomes: {
        devicesProvided: 0,
        peopleConnected: 0,
        skillsCertified: 0,
        centeresEstablished: 0
      },
      stakeholderSatisfaction: 0,
      description: 'Comprehensive digital inclusion program providing technology access, digital literacy training, and skills development for underserved communities.',
      riskLevel: 'Medium',
      socialLicense: 0,
      lastAssessment: '2025-09-01T16:00:00Z'
    },
    {
      id: 'cep-005',
      name: 'Youth Leadership and Civic Engagement',
      category: 'Youth Development',
      region: 'MEA',
      status: 'completed',
      startDate: '2024-09-01T00:00:00Z',
      endDate: '2025-08-31T23:59:59Z',
      budget: 520000,
      impactScore: 94.2,
      participantCount: 456,
      communityPartners: ['Youth Leadership Council', 'Civic Education Institute', 'Community Action Network'],
      sdgAlignment: ['SDG 16: Peace and Justice', 'SDG 4: Quality Education', 'SDG 17: Partnerships'],
      outcomes: {
        youthTrained: 456,
        projectsImplemented: 23,
        communityImpactPoints: 15,
        leadershipCertified: 289
      },
      stakeholderSatisfaction: 96.8,
      description: 'Youth leadership development program focusing on civic engagement, community organizing, and social entrepreneurship.',
      riskLevel: 'Low',
      socialLicense: 97.4,
      lastAssessment: '2025-08-25T13:15:00Z'
    }
  ],

  // Stakeholder Groups
  stakeholderGroups: [
    {
      id: 'sg-001',
      name: 'Indigenous Communities',
      type: 'Cultural',
      region: 'NA',
      memberCount: 12450,
      influenceLevel: 'High',
      engagementFrequency: 'Weekly',
      satisfactionScore: 87.6,
      concerns: [
        { issue: 'Cultural Heritage Protection', severity: 'High', status: 'Ongoing' },
        { issue: 'Land Rights Recognition', severity: 'High', status: 'Resolved' },
        { issue: 'Traditional Knowledge Sharing', severity: 'Medium', status: 'In Progress' }
      ],
      communicationChannels: ['Traditional Councils', 'Community Assemblies', 'Digital Platform'],
      lastEngagement: '2025-09-20T10:30:00Z',
      nextEngagement: '2025-09-27T14:00:00Z'
    },
    {
      id: 'sg-002',
      name: 'Local Business Community',
      type: 'Economic',
      region: 'LATAM',
      memberCount: 3420,
      influenceLevel: 'Medium',
      engagementFrequency: 'Monthly',
      satisfactionScore: 82.3,
      concerns: [
        { issue: 'Local Procurement Opportunities', severity: 'Medium', status: 'In Progress' },
        { issue: 'Skills Development Programs', severity: 'Low', status: 'Completed' },
        { issue: 'Infrastructure Development', severity: 'High', status: 'Planning' }
      ],
      communicationChannels: ['Business Forums', 'Chamber of Commerce', 'Online Portal'],
      lastEngagement: '2025-09-12T16:45:00Z',
      nextEngagement: '2025-10-15T09:00:00Z'
    },
    {
      id: 'sg-003',
      name: 'Environmental Groups',
      type: 'Environmental',
      region: 'EU',
      memberCount: 5670,
      influenceLevel: 'High',
      engagementFrequency: 'Bi-weekly',
      satisfactionScore: 79.8,
      concerns: [
        { issue: 'Biodiversity Conservation', severity: 'High', status: 'Ongoing' },
        { issue: 'Water Resource Management', severity: 'Medium', status: 'In Progress' },
        { issue: 'Carbon Emissions Reduction', severity: 'High', status: 'In Progress' }
      ],
      communicationChannels: ['Environmental Summits', 'Working Groups', 'Consultation Sessions'],
      lastEngagement: '2025-09-18T11:20:00Z',
      nextEngagement: '2025-10-02T14:30:00Z'
    },
    {
      id: 'sg-004',
      name: 'Youth Organizations',
      type: 'Social',
      region: 'APAC',
      memberCount: 8934,
      influenceLevel: 'Medium',
      engagementFrequency: 'Weekly',
      satisfactionScore: 91.4,
      concerns: [
        { issue: 'Education and Training Access', severity: 'Medium', status: 'In Progress' },
        { issue: 'Employment Opportunities', severity: 'High', status: 'Ongoing' },
        { issue: 'Technology Access', severity: 'Low', status: 'Completed' }
      ],
      communicationChannels: ['Youth Councils', 'Social Media', 'Peer Networks'],
      lastEngagement: '2025-09-19T15:00:00Z',
      nextEngagement: '2025-09-26T10:00:00Z'
    },
    {
      id: 'sg-005',
      name: 'Religious and Cultural Leaders',
      type: 'Cultural',
      region: 'MEA',
      memberCount: 2340,
      influenceLevel: 'High',
      engagementFrequency: 'Monthly',
      satisfactionScore: 94.7,
      concerns: [
        { issue: 'Cultural Sensitivity in Operations', severity: 'Medium', status: 'Resolved' },
        { issue: 'Community Values Alignment', severity: 'Low', status: 'Ongoing' },
        { issue: 'Interfaith Dialogue Support', severity: 'Low', status: 'Completed' }
      ],
      communicationChannels: ['Interfaith Councils', 'Traditional Meetings', 'Community Ceremonies'],
      lastEngagement: '2025-09-14T12:00:00Z',
      nextEngagement: '2025-10-12T14:00:00Z'
    }
  ],

  // Community Impact Metrics
  impactMetrics: {
    socialLicenseIndex: 89.2,
    communityTrustScore: 86.8,
    stakeholderSatisfaction: 88.4,
    conflictResolutionRate: 94.6,
    communityBenefitRatio: 3.4,
    localEmploymentRate: 67.8,
    localProcurementPercentage: 42.6,
    capacityBuildingParticipation: 78.9,
    grievanceResolutionTime: 12.4, // days
    communityInvestmentROI: 2.8
  },

  // Grievance Management System
  grievances: [
    {
      id: 'gr-001',
      title: 'Water Resource Concerns',
      category: 'Environmental',
      severity: 'High',
      status: 'In Progress',
      submittedBy: 'Local Environmental Committee',
      submittedDate: '2025-08-15T09:30:00Z',
      description: 'Community concerns about water table impact from operations',
      assignedTo: 'Environmental Team',
      expectedResolution: '2025-10-30T23:59:59Z',
      actions: [
        { date: '2025-08-16T14:00:00Z', action: 'Initial assessment conducted', responsible: 'Env. Manager' },
        { date: '2025-08-25T16:30:00Z', action: 'Hydrogeological study initiated', responsible: 'External Consultant' },
        { date: '2025-09-10T11:00:00Z', action: 'Community consultation meeting held', responsible: 'Community Relations' }
      ],
      resolution: null,
      satisfactionRating: 0
    },
    {
      id: 'gr-002',
      title: 'Local Employment Opportunities',
      category: 'Economic',
      severity: 'Medium',
      status: 'Resolved',
      submittedBy: 'Business Development Council',
      submittedDate: '2025-07-20T14:15:00Z',
      description: 'Request for increased local hiring and supplier preferences',
      assignedTo: 'HR and Procurement',
      expectedResolution: '2025-09-15T23:59:59Z',
      actions: [
        { date: '2025-07-22T10:00:00Z', action: 'Meeting with local business leaders', responsible: 'HR Director' },
        { date: '2025-08-05T15:30:00Z', action: 'Local hiring policy updated', responsible: 'HR Team' },
        { date: '2025-09-01T09:00:00Z', action: 'Supplier development program launched', responsible: 'Procurement' }
      ],
      resolution: 'Local hiring increased to 67.8%, supplier development program established',
      satisfactionRating: 8.4
    },
    {
      id: 'gr-003',
      title: 'Cultural Site Protection',
      category: 'Cultural',
      severity: 'High',
      status: 'Resolved',
      submittedBy: 'Traditional Council',
      submittedDate: '2025-06-10T11:45:00Z',
      description: 'Protection of sacred sites and cultural artifacts in project area',
      assignedTo: 'Cultural Heritage Team',
      expectedResolution: '2025-08-10T23:59:59Z',
      actions: [
        { date: '2025-06-12T16:00:00Z', action: 'Cultural heritage assessment initiated', responsible: 'Heritage Specialist' },
        { date: '2025-06-30T14:30:00Z', action: 'Sacred site mapping completed', responsible: 'Archaeological Team' },
        { date: '2025-07-25T13:00:00Z', action: 'Protection protocol implemented', responsible: 'Operations Team' }
      ],
      resolution: 'Cultural heritage management plan implemented, all sacred sites protected',
      satisfactionRating: 9.6
    }
  ],

  // Community Benefit Programs
  benefitPrograms: [
    {
      id: 'cbp-001',
      name: 'Community Development Fund',
      type: 'Financial',
      totalBudget: 5600000,
      allocatedAmount: 4200000,
      spentAmount: 3680000,
      beneficiaryCount: 15670,
      focusAreas: ['Education', 'Healthcare', 'Infrastructure', 'Economic Development'],
      achievements: [
        { area: 'Education', metric: 'Schools built', value: 12, target: 15 },
        { area: 'Healthcare', metric: 'Clinics established', value: 8, target: 10 },
        { area: 'Infrastructure', metric: 'Roads improved (km)', value: 45.6, target: 50 },
        { area: 'Economic Development', metric: 'Businesses supported', value: 234, target: 200 }
      ],
      lastReview: '2025-09-01T00:00:00Z',
      nextReview: '2025-12-01T00:00:00Z'
    },
    {
      id: 'cbp-002',
      name: 'Skills Development and Training',
      type: 'Capacity Building',
      totalBudget: 1200000,
      allocatedAmount: 1200000,
      spentAmount: 890000,
      beneficiaryCount: 2340,
      focusAreas: ['Technical Skills', 'Digital Literacy', 'Entrepreneurship', 'Leadership'],
      achievements: [
        { area: 'Technical Skills', metric: 'People trained', value: 1450, target: 1500 },
        { area: 'Digital Literacy', metric: 'Digital skills certified', value: 890, target: 800 },
        { area: 'Entrepreneurship', metric: 'Businesses launched', value: 78, target: 100 },
        { area: 'Leadership', metric: 'Leaders developed', value: 123, target: 150 }
      ],
      lastReview: '2025-08-15T00:00:00Z',
      nextReview: '2025-11-15T00:00:00Z'
    },
    {
      id: 'cbp-003',
      name: 'Environmental Stewardship Program',
      type: 'Environmental',
      totalBudget: 890000,
      allocatedAmount: 890000,
      spentAmount: 567000,
      beneficiaryCount: 5670,
      focusAreas: ['Conservation', 'Restoration', 'Education', 'Research'],
      achievements: [
        { area: 'Conservation', metric: 'Hectares protected', value: 2340, target: 2500 },
        { area: 'Restoration', metric: 'Areas restored', value: 890, target: 1000 },
        { area: 'Education', metric: 'People educated', value: 3450, target: 3000 },
        { area: 'Research', metric: 'Studies completed', value: 12, target: 15 }
      ],
      lastReview: '2025-09-10T00:00:00Z',
      nextReview: '2025-12-10T00:00:00Z'
    }
  ],

  // KPI Monitoring
  kpiMonitoring: [
    {
      id: 'kpi-001',
      name: 'Social License to Operate Index',
      current: 89.2,
      target: 92.0,
      trend: 'Improving',
      change: '+2.4%',
      category: 'Social License'
    },
    {
      id: 'kpi-002',
      name: 'Community Trust Score',
      current: 86.8,
      target: 90.0,
      trend: 'Stable',
      change: '+0.6%',
      category: 'Trust & Relationships'
    },
    {
      id: 'kpi-003',
      name: 'Stakeholder Satisfaction',
      current: 88.4,
      target: 91.0,
      trend: 'Improving',
      change: '+1.8%',
      category: 'Stakeholder Relations'
    },
    {
      id: 'kpi-004',
      name: 'Grievance Resolution Rate',
      current: 94.6,
      target: 95.0,
      trend: 'Stable',
      change: '+0.3%',
      category: 'Conflict Resolution'
    },
    {
      id: 'kpi-005',
      name: 'Local Employment Rate',
      current: 67.8,
      target: 75.0,
      trend: 'Improving',
      change: '+3.2%',
      category: 'Economic Impact'
    },
    {
      id: 'kpi-006',
      name: 'Community Investment ROI',
      current: 2.8,
      target: 3.2,
      trend: 'Improving',
      change: '+0.4',
      category: 'Investment Effectiveness'
    }
  ],

  // ESG Compliance Framework
  complianceFramework: {
    gri413: {
      score: 87.4,
      lastAssessment: '2025-08-30T00:00:00Z',
      nextAssessment: '2025-11-30T00:00:00Z',
      requirements: [
        { requirement: 'Operations with community engagement', compliance: 94.2 },
        { requirement: 'Operations with local community development programs', compliance: 86.8 },
        { requirement: 'Operations with negative impacts on local communities', compliance: 78.9 }
      ]
    },
    sasbSocialCapital: {
      score: 84.6,
      lastAssessment: '2025-09-05T00:00:00Z',
      nextAssessment: '2025-12-05T00:00:00Z',
      requirements: [
        { requirement: 'Community relations management', compliance: 89.3 },
        { requirement: 'Management of rights of indigenous peoples', compliance: 91.7 },
        { requirement: 'Process to manage risks and opportunities', compliance: 72.4 }
      ]
    },
    iso26000: {
      score: 82.1,
      lastAssessment: '2025-08-15T00:00:00Z',
      nextAssessment: '2025-11-15T00:00:00Z',
      requirements: [
        { requirement: 'Community involvement', compliance: 88.6 },
        { requirement: 'Education and culture', compliance: 79.4 },
        { requirement: 'Employment creation and skills development', compliance: 84.2 },
        { requirement: 'Community investment', compliance: 78.9 }
      ]
    }
  }
};

// Local storage management
function loadESGCommunityEngagementData() {
  try {
    const stored = localStorage.getItem(ESG_COMMUNITY_ENGAGEMENT_KEY);
    return stored ? JSON.parse(stored) : esgCommunityEngagementSeed;
  } catch (error) {
    console.warn('Failed to load ESG community engagement data:', error);
    return esgCommunityEngagementSeed;
  }
}

function saveESGCommunityEngagementData(data) {
  try {
    localStorage.setItem(ESG_COMMUNITY_ENGAGEMENT_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save ESG community engagement data:', error);
  }
}

// Application state
let esgCommunityEngagementState = loadESGCommunityEngagementData();
const esgCommunityEngagementSubscribers = new Set();

// State management functions
function publishESGCommunityEngagementUpdate() {
  saveESGCommunityEngagementData(esgCommunityEngagementState);
  esgCommunityEngagementSubscribers.forEach(callback => {
    try {
      callback(esgCommunityEngagementState);
    } catch (error) {
      console.error('Subscriber callback failed:', error);
    }
  });
}

// Subscription management
export function subscribe(callback) {
  esgCommunityEngagementSubscribers.add(callback);
  callback(esgCommunityEngagementState);
  return () => esgCommunityEngagementSubscribers.delete(callback);
}

// Data access functions
export function getESGCommunityEngagementOverview() {
  const programs = esgCommunityEngagementState.programs;
  const stakeholders = esgCommunityEngagementState.stakeholderGroups;
  const metrics = esgCommunityEngagementState.impactMetrics;
  const grievances = esgCommunityEngagementState.grievances;
  
  return {
    totalPrograms: programs.length,
    activePrograms: programs.filter(p => p.status === 'active').length,
    totalBudget: programs.reduce((sum, p) => sum + p.budget, 0),
    totalParticipants: programs.reduce((sum, p) => sum + p.participantCount, 0),
    averageImpactScore: programs.reduce((sum, p) => sum + p.impactScore, 0) / programs.length,
    stakeholderGroups: stakeholders.length,
    totalStakeholders: stakeholders.reduce((sum, s) => sum + s.memberCount, 0),
    averageSatisfaction: stakeholders.reduce((sum, s) => sum + s.satisfactionScore, 0) / stakeholders.length,
    socialLicenseIndex: metrics.socialLicenseIndex,
    communityTrustScore: metrics.communityTrustScore,
    openGrievances: grievances.filter(g => g.status !== 'Resolved').length,
    resolvedGrievances: grievances.filter(g => g.status === 'Resolved').length
  };
}

export function getPrograms() {
  return esgCommunityEngagementState.programs;
}

export function getStakeholderGroups() {
  return esgCommunityEngagementState.stakeholderGroups;
}

export function getImpactMetrics() {
  return esgCommunityEngagementState.impactMetrics;
}

export function getGrievances() {
  return esgCommunityEngagementState.grievances;
}

export function getBenefitPrograms() {
  return esgCommunityEngagementState.benefitPrograms;
}

export function getKPIMonitoring() {
  return esgCommunityEngagementState.kpiMonitoring;
}

export function getComplianceFramework() {
  return esgCommunityEngagementState.complianceFramework;
}

export function getProgramById(programId) {
  return esgCommunityEngagementState.programs.find(p => p.id === programId);
}

export function getStakeholderGroupById(groupId) {
  return esgCommunityEngagementState.stakeholderGroups.find(s => s.id === groupId);
}

export function getGrievanceById(grievanceId) {
  return esgCommunityEngagementState.grievances.find(g => g.id === grievanceId);
}

// Data analysis functions
export function calculateCommunityImpactTrends() {
  const programs = esgCommunityEngagementState.programs;
  const stakeholders = esgCommunityEngagementState.stakeholderGroups;
  
  return {
    programImpact: programs.map(p => ({
      name: p.name,
      impact: p.impactScore,
      participants: p.participantCount,
      budget: p.budget,
      roi: p.participantCount > 0 ? p.impactScore / (p.budget / 1000) : 0
    })),
    stakeholderSatisfaction: stakeholders.map(s => ({
      group: s.name,
      satisfaction: s.satisfactionScore,
      influence: s.influenceLevel,
      engagement: s.engagementFrequency
    })),
    regionalDistribution: programs.reduce((acc, p) => {
      acc[p.region] = (acc[p.region] || 0) + 1;
      return acc;
    }, {})
  };
}

export function getHighImpactPrograms() {
  return esgCommunityEngagementState.programs
    .filter(p => p.impactScore > 85)
    .sort((a, b) => b.impactScore - a.impactScore);
}

export function getCriticalStakeholders() {
  return esgCommunityEngagementState.stakeholderGroups
    .filter(s => s.influenceLevel === 'High' && s.satisfactionScore < 85)
    .sort((a, b) => a.satisfactionScore - b.satisfactionScore);
}

export function getGrievancesByStatus(status) {
  return esgCommunityEngagementState.grievances.filter(g => g.status === status);
}

// Data manipulation functions
export function updateProgramProgress(programId, updates) {
  const programIndex = esgCommunityEngagementState.programs.findIndex(p => p.id === programId);
  if (programIndex >= 0) {
    esgCommunityEngagementState.programs[programIndex] = {
      ...esgCommunityEngagementState.programs[programIndex],
      ...updates,
      lastAssessment: new Date().toISOString()
    };
    publishESGCommunityEngagementUpdate();
  }
}

export function updateStakeholderSatisfaction(groupId, satisfactionScore) {
  const groupIndex = esgCommunityEngagementState.stakeholderGroups.findIndex(s => s.id === groupId);
  if (groupIndex >= 0) {
    esgCommunityEngagementState.stakeholderGroups[groupIndex].satisfactionScore = satisfactionScore;
    esgCommunityEngagementState.stakeholderGroups[groupIndex].lastEngagement = new Date().toISOString();
    publishESGCommunityEngagementUpdate();
  }
}

export function addNewProgram(program) {
  const newProgram = {
    id: `cep-${Date.now().toString(36)}`,
    ...program,
    status: program.status || 'planning',
    impactScore: 0,
    participantCount: 0,
    stakeholderSatisfaction: 0,
    socialLicense: 0,
    lastAssessment: new Date().toISOString()
  };
  esgCommunityEngagementState.programs.push(newProgram);
  publishESGCommunityEngagementUpdate();
  return newProgram;
}

export function resolveGrievance(grievanceId, resolution, satisfactionRating) {
  const grievanceIndex = esgCommunityEngagementState.grievances.findIndex(g => g.id === grievanceId);
  if (grievanceIndex >= 0) {
    esgCommunityEngagementState.grievances[grievanceIndex] = {
      ...esgCommunityEngagementState.grievances[grievanceIndex],
      status: 'Resolved',
      resolution,
      satisfactionRating,
      resolutionDate: new Date().toISOString()
    };
    publishESGCommunityEngagementUpdate();
  }
}

export function addGrievanceAction(grievanceId, action) {
  const grievanceIndex = esgCommunityEngagementState.grievances.findIndex(g => g.id === grievanceId);
  if (grievanceIndex >= 0) {
    const newAction = {
      date: new Date().toISOString(),
      ...action
    };
    esgCommunityEngagementState.grievances[grievanceIndex].actions.push(newAction);
    publishESGCommunityEngagementUpdate();
  }
}

// Reporting functions
export function generateCommunityEngagementReport(programId = null, groupId = null) {
  const report = {
    reportId: `ce-${Date.now()}`,
    generatedAt: new Date().toISOString(),
    reportType: programId ? 'Program' : groupId ? 'Stakeholder Group' : 'Comprehensive',
    summary: getESGCommunityEngagementOverview()
  };

  if (programId) {
    const program = getProgramById(programId);
    report.program = {
      program,
      impactAnalysis: calculateCommunityImpactTrends().programImpact.find(p => p.name === program?.name),
      relatedGrievances: esgCommunityEngagementState.grievances.filter(g => 
        g.description.toLowerCase().includes(program?.category.toLowerCase())
      )
    };
  } else if (groupId) {
    const group = getStakeholderGroupById(groupId);
    report.stakeholderGroup = {
      group,
      engagementHistory: group?.concerns || [],
      satisfactionTrend: 'Stable' // Could be calculated from historical data
    };
  } else {
    report.comprehensive = {
      programs: esgCommunityEngagementState.programs,
      stakeholderGroups: esgCommunityEngagementState.stakeholderGroups,
      impactMetrics: esgCommunityEngagementState.impactMetrics,
      grievances: esgCommunityEngagementState.grievances,
      benefitPrograms: esgCommunityEngagementState.benefitPrograms,
      kpis: esgCommunityEngagementState.kpiMonitoring,
      compliance: esgCommunityEngagementState.complianceFramework
    };
  }

  return report;
}

export function exportCommunityEngagementData(format = 'json', filterCriteria = {}) {
  let exportData = { ...esgCommunityEngagementState };

  // Apply filters
  if (filterCriteria.region) {
    exportData.programs = exportData.programs.filter(p => p.region === filterCriteria.region);
    exportData.stakeholderGroups = exportData.stakeholderGroups.filter(s => s.region === filterCriteria.region);
  }
  if (filterCriteria.category) {
    exportData.programs = exportData.programs.filter(p => 
      p.category.toLowerCase().includes(filterCriteria.category.toLowerCase())
    );
  }
  if (filterCriteria.status) {
    exportData.programs = exportData.programs.filter(p => p.status === filterCriteria.status);
  }

  return {
    format,
    exportedAt: new Date().toISOString(),
    recordCount: {
      programs: exportData.programs.length,
      stakeholderGroups: exportData.stakeholderGroups.length,
      grievances: exportData.grievances.length
    },
    data: exportData
  };
}

// Data refresh function
export async function refreshCommunityEngagementData() {
  // Simulate API call delay
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  // Update timestamps and simulate minor data changes
  esgCommunityEngagementState.programs.forEach(program => {
    program.lastAssessment = new Date().toISOString();
    if (program.status === 'active') {
      program.impactScore += (Math.random() - 0.5) * 3; // Small random adjustment
      program.impactScore = Math.max(0, Math.min(100, program.impactScore));
    }
  });
  
  // Update stakeholder satisfaction with small changes
  esgCommunityEngagementState.stakeholderGroups.forEach(group => {
    const change = (Math.random() - 0.5) * 2; // ±1% change
    group.satisfactionScore = Math.max(0, Math.min(100, group.satisfactionScore + change));
    group.lastEngagement = new Date().toISOString();
  });
  
  // Update KPI values with small changes
  esgCommunityEngagementState.kpiMonitoring.forEach(kpi => {
    const change = (Math.random() - 0.5) * 2; // ±1% change
    kpi.current = Math.max(0, Math.min(100, kpi.current + change));
    kpi.change = change >= 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
  });
  
  publishESGCommunityEngagementUpdate();
}